import React from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Button, Field } from '@/nova/components';

import type { LandingScene } from '../../landing-page-api';
import * as S from './LandingPageComposer.styles';

/** How many characters a scene shows (left and right of the game). */
const CHARACTER_SLOTS = 2;

/** One replaceable layer of the scene. */
type SlotId =
  | { kind: 'background'; which: 'desktop' | 'mobile' }
  | { kind: 'character'; index: number }
  | { kind: 'part'; key: string };

const slotKey = (slot: SlotId): string =>
  slot.kind === 'background' ? `bg:${slot.which}` : slot.kind === 'character' ? `ch:${slot.index}` : `part:${slot.key}`;

/** The art keys the games draw, in the owner's words; another key is shown as its own words ("wheel_frame" ->
 *  "Wheel frame"). */
const partLabel = (key: string, t: TFunction): string => {
  switch (key) {
    case 'wheel_frame':
      return t('Wheel frame');
    case 'wheel_frame_lit':
      return t('Wheel frame, lit');
    case 'wheel_hub':
      return t('Wheel hub');
    case 'wheel_pin':
      return t('Wheel pointer');
    case 'wheel_disc':
      return t('Wheel disc');
    case 'plate':
      return t('Prize plate');
    case 'chest_closed':
      return t('Closed chest');
    case 'chest_open':
      return t('Open chest');
    default: {
      const words = key.replace(/[_-]+/g, ' ').trim();
      return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
    }
  }
};

const urlOf = (scene: LandingScene, slot: SlotId): string => {
  if (slot.kind === 'background')
    return slot.which === 'desktop' ? scene.background_desktop_url : scene.background_mobile_url;
  if (slot.kind === 'character') return scene.characters[slot.index] ?? '';
  return scene.parts[slot.key] ?? '';
};

/** The scene with one layer's URL set ('' clears it). Characters keep no gaps: a URL past the last one is added after
 *  it, a cleared one closes up. Pure. */
const withUrl = (scene: LandingScene, slot: SlotId, url: string): LandingScene => {
  if (slot.kind === 'background')
    return slot.which === 'desktop'
      ? { ...scene, background_desktop_url: url }
      : { ...scene, background_mobile_url: url };
  if (slot.kind === 'character') {
    const chars = scene.characters.slice(0, CHARACTER_SLOTS);
    if (!url) return { ...scene, characters: chars.filter((_, i) => i !== slot.index) };
    if (slot.index < chars.length) chars[slot.index] = url;
    else if (chars.length < CHARACTER_SLOTS) chars.push(url);
    return { ...scene, characters: chars };
  }
  const parts = { ...scene.parts };
  if (url) parts[slot.key] = url;
  else delete parts[slot.key];
  return { ...scene, parts };
};

export interface SceneLayersProps {
  scene: LandingScene;
  /** The game's own art keys the template draws (its `scene_parts`; none on the regular page). */
  parts: string[];
  /** The template draws floating symbols (a full-screen game; the regular page draws its backgrounds and characters
   *  only). */
  symbols?: boolean;
  disabled: boolean;
  /** Apply an edit to the CURRENT scene (the composer's latest). */
  onChange: (fn: (cur: LandingScene) => LandingScene) => void;
}

/**
 * A template's scene: the layers Lumio draws in the brand's art style (the desktop and mobile backgrounds, the
 * characters, and on a full-screen game its own parts and the floating symbols), each a small thumbnail or a "Not
 * generated yet" tile. A press on a layer opens its image URL, so the owner can put their own art in its place
 * (cleared, the next Generate draws it).
 */
export const SceneLayers = ({
  scene,
  parts,
  symbols = true,
  disabled,
  onChange,
}: SceneLayersProps): React.ReactElement => {
  const { t } = useTranslation();
  const [picked, setPicked] = React.useState<SlotId | null>(null);

  const slots: { id: SlotId; label: string }[] = [
    { id: { kind: 'background', which: 'desktop' }, label: t('Background, desktop') },
    { id: { kind: 'background', which: 'mobile' }, label: t('Background, mobile') },
    ...Array.from({ length: CHARACTER_SLOTS }, (_, index) => ({
      id: { kind: 'character', index } as SlotId,
      label: t('Character {{n}}', { n: index + 1 }),
    })),
    ...parts.map((key) => ({ id: { kind: 'part', key } as SlotId, label: partLabel(key, t) })),
  ];
  const pickedSlot = picked ? slots.find((s) => slotKey(s.id) === slotKey(picked)) : undefined;

  const tile = (url: string): React.ReactElement =>
    url ? (
      <S.SceneThumb src={url} alt="" loading="lazy" />
    ) : (
      <S.ScenePlaceholder>
        <S.ScenePlaceholderText text="Not generated yet" />
      </S.ScenePlaceholder>
    );

  return (
    <S.Stack>
      <S.SceneGrid role="group" aria-label={t('Scene layers')}>
        {slots.map((slot) => {
          const key = slotKey(slot.id);
          const selected = picked !== null && slotKey(picked) === key;
          return (
            <S.SceneSlot
              key={key}
              type="button"
              $selected={selected}
              aria-pressed={selected}
              title={slot.label}
              disabled={disabled}
              onClick={() =>
                setPicked(
                  selected
                    ? null
                    : slot.id.kind === 'character'
                      ? // An empty character slot after an empty one edits the next free one (no gaps).
                        { kind: 'character', index: Math.min(slot.id.index, scene.characters.length) }
                      : slot.id,
                )
              }
            >
              {tile(urlOf(scene, slot.id))}
              <S.SceneLabel raw text={slot.label} />
            </S.SceneSlot>
          );
        })}
      </S.SceneGrid>

      {pickedSlot ? (
        <S.Stack>
          <Field
            label={t('Image URL for {{layer}}', { layer: pickedSlot.label })}
            value={urlOf(scene, pickedSlot.id)}
            onChange={(value) => onChange((cur) => withUrl(cur, pickedSlot.id, value.trim()))}
            placeholder="https://"
            disabled={disabled}
          />
          <S.SubjectAction>
            <Button variant="text" label={t('Done')} onClick={() => setPicked(null)} />
          </S.SubjectAction>
        </S.Stack>
      ) : null}

      {symbols ? (
        <>
          <S.SceneGroupLabel text="Symbols" />
          {scene.symbols.length > 0 ? (
            <S.SceneGrid>
              {scene.symbols.map((url, index) => (
                <S.SceneTile key={`${url}:${index}`}>
                  <S.SceneThumb src={url} alt="" loading="lazy" />
                </S.SceneTile>
              ))}
            </S.SceneGrid>
          ) : (
            <S.Hint text="Not generated yet. Lumio draws up to 10 floating symbols (coins, chips, gems) for the scene." />
          )}
        </>
      ) : null}
      <S.Hint text="Lumio draws every layer in your art style when you generate with Scene art on. Press a layer to use your own image instead." />
    </S.Stack>
  );
};

export default SceneLayers;
